'use client';

import { exists } from 'src/utils/exists/exists';
import { formatPhoneNumber } from 'src/utils/formatPhoneNumber/formatPhoneNumber';
import { hasContent } from 'src/utils/hasContent/hasContent';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { FC } from 'react';
import Link from 'next/link';
import InfoUnavailable from '../InfoUnavailable';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { Box, Typography, EmailConfidenceChip } from 'src/components/PulseUI';

type DetailsDisplayProps = {
  items: any[];
  options?: { [key: string]: string };
  allDetails?: boolean;
};

const DetailsDisplay: FC<DetailsDisplayProps> = ({
  items,
  options,
  allDetails,
}) => {
  const { entityDetails } = useProfile();

  const displayFields = ['npi', 'email', 'phone'];
  const emailSource = entityDetails?.email_source;
  const newPhone = entityDetails?.phone;

  // Filter the items to only include allowed fields
  const filteredItems =
    items?.filter((item) =>
      displayFields.includes(item.title?.toLowerCase())
    ) || [];

  // Add GPO items to the end if they exist
  const gpoItems =
    items?.filter(
      (item) => item.title === 'Group Purchasing Organization (GPO)'
    ) || [];
  const displayItems = allDetails ? items : [...filteredItems, ...gpoItems];

  return (
    <>
      {exists(displayItems) ? (
        displayItems.map((item, i) => {
          const isLast = i === displayItems.length - 1;
          const isPhone = item.title === 'Phone';
          const formattedValue = isPhone
            ? formatPhoneNumber(newPhone)
            : item.value && hasContent(item.value)
            ? item.value
            : 'Not available';

          return (
            <Box key={`${item.title}-${i}`} className="flex flex-col">
              {exists(options) ? (
                <Typography
                  variant="body2"
                  bold
                  className={`${isLast ? 'pb-0' : 'pb-4'} text-link`}
                >
                  <Link
                    href={`${options.basePath}${item[options.idKey]}`}
                    scroll={false}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {item[options.displayKey]}{' '}
                    <Icon
                      icon={faExternalLinkAlt}
                      className="h-4 w-4 text-link inline-flex ml-1"
                    />
                  </Link>
                </Typography>
              ) : (
                <>
                  <Box className="flex flex-row gap-2 items-center">
                    <Typography variant="body2" color="tertiary">
                      {item.title}
                    </Typography>
                    {item.title === 'Email' && emailSource && item.value && (
                      <EmailConfidenceChip source={emailSource} />
                    )}
                  </Box>
                  <Box>
                    {Array.isArray(formattedValue) &&
                    hasContent(formattedValue) ? (
                      formattedValue.map((value, index) => (
                        <Typography variant="body2" color="primary" bold key={index}>
                          {value || 'Not available'}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="primary" bold>
                        {formattedValue || 'Not available'}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          );
        })
      ) : (
        <InfoUnavailable />
      )}
    </>
  );
};

export default DetailsDisplay;
